import {
  bitrixConnectionResponseSchema,
  bitrixOpenlinesResponseSchema,
  bitrixOptionsResponseSchema,
  bitrixTestResponseSchema,
  bitrixUserLinksResponseSchema,
  conversationBitrixResponseSchema,
  setBitrixUserLinkResponseSchema,
  type BitrixConnection,
  type BitrixOpenlines,
  type BitrixOptionsResponse,
  type BitrixSettings,
  type BitrixTestResponse,
  type BitrixUserLink,
  type ConversationBitrix,
  type SaveBitrixConnectionBody,
} from '@chatapp-inbox/shared';
import { request } from './http';

// Integração com o Bitrix24 (docs/bitrix.md). As rotas de administração são SÓ DO ADMINISTRADOR.

/** A conexão com o Bitrix (nunca traz o segredo do aplicativo nem tokens). */
export async function getBitrix(signal?: AbortSignal): Promise<BitrixConnection> {
  const { connection } = await request('/admin/bitrix', {
    schema: bitrixConnectionResponseSchema,
    ...(signal ? { signal } : {}),
  });
  return connection;
}

/** Guarda o domínio, o ID e o segredo do aplicativo. Segredo ausente MANTÉM o guardado. */
export async function saveBitrixConnection(
  body: SaveBitrixConnectionBody,
): Promise<BitrixConnection> {
  const { connection } = await request('/admin/bitrix/connection', {
    method: 'PUT',
    body,
    schema: bitrixConnectionResponseSchema,
  });
  return connection;
}

export async function saveBitrixSettings(body: BitrixSettings): Promise<BitrixConnection> {
  const { connection } = await request('/admin/bitrix/settings', {
    method: 'PUT',
    body,
    schema: bitrixConnectionResponseSchema,
  });
  return connection;
}

/** Desconecta: o servidor esquece o segredo e os tokens (os vínculos das conversas ficam). */
export async function deleteBitrixConnection(): Promise<void> {
  await request('/admin/bitrix/connection', { method: 'DELETE' });
}

/** Usuários, etapas e origens do Bitrix, para as escolhas da tela. */
export function getBitrixOptions(signal?: AbortSignal): Promise<BitrixOptionsResponse> {
  return request('/admin/bitrix/options', {
    schema: bitrixOptionsResponseSchema,
    ...(signal ? { signal } : {}),
  });
}

/** Confere que o acesso funciona (um pedido simples ao Bitrix). */
export function testBitrix(): Promise<BitrixTestResponse> {
  return request('/admin/bitrix/test', { method: 'POST', schema: bitrixTestResponseSchema });
}

const conversationPath = (id: string): string => `/conversations/${encodeURIComponent(id)}/bitrix`;

/** O Lead da conversa (ou o motivo de não haver). */
export async function getConversationBitrix(
  conversationId: string,
  signal?: AbortSignal,
): Promise<ConversationBitrix> {
  const { bitrix } = await request(conversationPath(conversationId), {
    schema: conversationBitrixResponseSchema,
    ...(signal ? { signal } : {}),
  });
  return bitrix;
}

/** Pede para conferir de novo, já. */
export async function syncConversationBitrix(conversationId: string): Promise<ConversationBitrix> {
  const { bitrix } = await request(`${conversationPath(conversationId)}/sync`, {
    method: 'POST',
    schema: conversationBitrixResponseSchema,
  });
  return bitrix;
}

// O Canal Aberto: o bate-papo no card do Bitrix (docs/bitrix-openlines.md). Só administrador.

export async function getBitrixOpenlines(signal?: AbortSignal): Promise<BitrixOpenlines> {
  const { openlines } = await request('/admin/bitrix/openlines', {
    schema: bitrixOpenlinesResponseSchema,
    ...(signal ? { signal } : {}),
  });
  return openlines;
}

/** Liga ou desliga o envio das conversas para o chat do Bitrix. */
export async function setBitrixOpenlines(enabled: boolean): Promise<BitrixOpenlines> {
  const { openlines } = await request('/admin/bitrix/openlines', {
    method: 'PUT',
    body: { enabled },
    schema: bitrixOpenlinesResponseSchema,
  });
  return openlines;
}

/** Liga ou desliga "assumir no CladvApp tira o bot da linha do Bitrix". */
export async function setBitrixHandoff(handoffOnAssign: boolean): Promise<BitrixOpenlines> {
  const { openlines } = await request('/admin/bitrix/openlines', {
    method: 'PUT',
    body: { handoffOnAssign },
    schema: bitrixOpenlinesResponseSchema,
  });
  return openlines;
}

/** Deixa o portal pronto: registra o conector, assina os eventos e liga uma linha aberta por número. */
export async function applyBitrixOpenlines(): Promise<BitrixOpenlines> {
  const { openlines } = await request('/admin/bitrix/openlines/apply', {
    method: 'POST',
    schema: bitrixOpenlinesResponseSchema,
  });
  return openlines;
}

/** Quem é quem no Bitrix: o usuário do Bitrix de cada usuário do CladvApp (só os que têm vínculo escolhido). */
export async function getBitrixUserLinks(signal?: AbortSignal): Promise<BitrixUserLink[]> {
  const { links } = await request('/admin/bitrix/user-links', {
    schema: bitrixUserLinksResponseSchema,
    ...(signal ? { signal } : {}),
  });
  return links;
}

/** Vincula o usuário do CladvApp a um usuário do Bitrix; `null` desfaz (volta ao e-mail). */
export async function setBitrixUserLink(
  userId: string,
  bitrixUserId: string | null,
): Promise<BitrixUserLink | null> {
  const { link } = await request(`/admin/bitrix/user-links/${encodeURIComponent(userId)}`, {
    method: 'PUT',
    body: { bitrixUserId },
    schema: setBitrixUserLinkResponseSchema,
  });
  return link;
}
